import React from 'react'
import {Box, Button, Card, Stack, Typography, useTheme} from "@mui/joy";
import {Notification} from "../models/notification";
import {BellIcon} from "lucide-react";
import {Link, useNavigate} from "react-router-dom";
import {appRoute} from "../utils/routes";
import {useNotification} from "../hooks/useNotification";
import {useTranslation} from "react-i18next";

type NotificationItemComponentProps = {
    notification: Notification,
    markedAsRead?: boolean
}

const NotificationItemComponent = (props: NotificationItemComponentProps) => {
    const {t} = useTranslation()
    const theme = useTheme()
    const notification = useNotification()
    const fontWeight = (props.notification.pending && !props.markedAsRead) ? "600" : "6400"
    const dateFormatter = new Intl.DateTimeFormat("en", {
        weekday: 'short',
        month: 'short',
        day: '2-digit'
    })
    const unread = props.notification.pending && !props.markedAsRead
    const action = props.notification?.action?.[0]

    // Opening the notification's target reads it, on the server too, so the badge stays down after a reload.
    const handleNotificationAction = () => {
        if (unread)
            notification.markAsRead(props.notification?._id)
    }

    return <Card variant={"soft"} color={"neutral"}
                 sx={{
                     paddingY: 2,
                     paddingX: 3,
                     backgroundColor: (props.notification.pending && !props.markedAsRead) ? "background.level1" : "transparent",
                     borderRadius: 12
                 }}>
        <Stack direction={"row"} gap={2} alignItems={"center"}>
            <BellIcon style={{
                fill: !(props.notification.pending && !props.markedAsRead) ? theme.palette.text.icon : theme.palette.text.primary,
                color: !(props.notification.pending && !props.markedAsRead) ? theme.palette.text.icon : theme.palette.text.primary,
                fontSize: 30
            }}/>

            <Stack flex={1}>
                <Stack direction={"row"} alignItems="center" width={"100%"} justifyContent={"space-between"}>
                    <Typography level={"title-md"} textOverflow={"ellipsis"}
                                maxWidth={"65%"}>{props.notification.title}</Typography>

                    <Stack gap={1} alignItems={"center"} direction={"row"}>
                        <Typography fontWeight={fontWeight}>
                            {isNaN(new Date(props.notification.date).getTime()) ? "—" : dateFormatter.format(new Date(props.notification.date))}
                        </Typography>
                        {(props.notification.pending && !props.markedAsRead) &&
                            <Box borderRadius={"100%"} width={10} height={10} sx={{backgroundColor: "primary.600"}}/>}
                    </Stack>
                </Stack>

                <Typography alignSelf={"flex-start"} level={"body-sm"} fontWeight={fontWeight}>
                    {props.notification.body}
                </Typography>
            </Stack>

        </Stack>

        <Stack paddingX={2} gap={1} direction={"row"} justifyContent={"space-between"}>
            {action?.target &&
                <Button component={Link} to={appRoute(action.target)}
                        size={"sm"} sx={{borderRadius: 20}} onClick={handleNotificationAction}>
                    {action.label}
                </Button>}

            {(props.notification.pending && !props.markedAsRead) &&
                <Button onClick={() => notification.markAsRead(props.notification?._id)} size={"sm"}
                        variant={"outlined"} color={"neutral"} sx={{borderRadius: 20}}>
                    {t("notificationItemActionLabel")}
                </Button>}
        </Stack>
    </Card>
}

export default NotificationItemComponent